import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { Button } from '@jeraisonne/ui';
import { PaperColumn, PaperPage } from '@jeraisonne/ui/paper';
import { AlertCircle, Loader2, X } from 'lucide-react';
import type { SchoolLevel } from '@jeraisonne/db';
import { LevelSelectionScreen } from '@/features/demo/screens/level-selection-screen';
import { DemoEndScreen } from '@/features/demo/screens/demo-end-screen';
import { GuidedIntroScreen } from '@/features/demo/screens/guided-intro-screen';
import { trackDemoEvent } from '@/features/demo/tracking';
import { demoExerciseFor } from '@/features/demo/demo-exercises';
import { LEVEL_LABEL } from '@/features/demo/types';
import { loadGuidedQuestion, type GuidedQuestion } from '@/features/demo/guided/guided-question';
import { ExercisePlayerV2 } from '@/features/exercise-player-v2';
import { markPaperTutorialSeen } from '@/features/scratch-sheet/sheet-tutorial';
import { markScratchTutorialFinished } from '@/features/scratch-tutorial/first-use';
import {
  readDecouverteProfile,
  saveDecouverteProfile,
} from '@/features/decouverte/decouverte-profile';
import { markDecouverteSeen } from '@/features/decouverte/decouverte-seen';
import { trackAppEvent } from '@/lib/analytics/track';

// le moteur v2 (packs compilés) n'est chargé qu'ici
const GuidedRound = lazy(() => import('@/features/demo/guided/guided-round'));

type Phase = 'level' | 'play' | 'end';

/**
 * Route `/demo` — démo sans compte, en trois temps :
 *
 *  1. le visiteur touche SA classe (6e → Terminale) ;
 *  2. un écran le prépare : « Raisonne pas à pas · Laisse-toi guider » (une fois par visite) ;
 *  3. il joue la vraie question de démo de cette classe, GUIDÉE : la feuille du moteur v2 sous la question, un
 *     coach collé au clavier qui montre où toucher (l'élément pulse en doré ; c'est toujours l'élève qui appuie)
 *     et fait sentir la puissance du moteur — il connaît le cours (l'ampoule), repère l'erreur classique et
 *     l'explique, propose les valeurs et les résultats, vérifie chaque ligne, marque la réponse trouvée. Le coach
 *     est le même pour toutes les classes (`coach.ts`) : une question qu'il ne sait pas mener jusqu'à la réponse
 *     se joue sans guide (player V2 en mode démo) ;
 *  4. l'inscription GRATUITE (aucun prix).
 *
 * Aucune authentification, rien n'est enregistré.
 */
export function DemoPage() {
  const navigate = useNavigate();
  const router = useRouter();
  const [phase, setPhase] = useState<Phase>('level');
  const [level, setLevel] = useState<SchoolLevel | null>(null);
  const [question, setQuestion] = useState<GuidedQuestion | null>(null);
  const [loadError, setLoadError] = useState(false);
  // la question se joue sans guide (pas de feuille, ou le coach ne la mène pas au bout)
  const [unguided, setUnguided] = useState(false);
  // incrémenté pour rejouer la question depuis le début
  const [round, setRound] = useState(0);
  // l'écran « Raisonne pas à pas · Laisse-toi guider » a été passé (une fois par visite)
  const [started, setStarted] = useState(false);

  useEffect(() => {
    trackDemoEvent({ type: 'demo_started' });
    // Ancienne démo (échauffement V1) : son état local ne sert plus.
    try {
      window.localStorage.removeItem('jeraisonne_demo_state');
    } catch {
      // stockage indisponible (navigation privée) : rien à effacer
    }
  }, []);

  // Pages « virtuelles » : l'entonnoir de la démo dans les statistiques.
  useEffect(() => {
    if (phase === 'level') trackAppEvent('page_view', '/demo/classe');
    if (phase === 'play') trackAppEvent('page_view', '/demo/question');
    if (phase === 'end') trackAppEvent('page_view', '/demo/fin');
    window.scrollTo({ top: 0 });
  }, [phase]);

  useEffect(() => {
    if (!level) return;
    const exerciseId = demoExerciseFor(level);
    let cancelled = false;
    setQuestion(null);
    setLoadError(false);
    setUnguided(false);
    if (!exerciseId) {
      setLoadError(true);
      return;
    }
    loadGuidedQuestion(exerciseId)
      .then((q) => {
        if (cancelled) return;
        if (!q) setLoadError(true);
        else {
          setQuestion(q);
          setUnguided(!q.model);
        }
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [level]);

  // l'écran de préparation laisse le temps de charger le moteur v2 (le même module que `GuidedRound`)
  useEffect(() => {
    if (question && !unguided) void import('@/features/demo/guided/guided-round').catch(() => {});
  }, [question, unguided]);

  const finish = useCallback(() => {
    trackDemoEvent({ type: 'demo_completed' });
    // les gestes sont acquis : la bulle de prise en main du player ne se rejoue pas
    markPaperTutorialSeen();
    setPhase('end');
  }, []);
  // l'exercice guidé EST la prise en main du brouillon : pas de didacticiel à l'inscription sur cet appareil
  const finishGuided = useCallback(() => {
    markScratchTutorialFinished();
    finish();
  }, [finish]);
  const playUnguided = useCallback(() => setUnguided(true), []);

  function pickLevel(selected: SchoolLevel) {
    setLevel(selected);
    setRound((r) => r + 1);
    setPhase('play');
    trackDemoEvent({ type: 'demo_level_selected', level: selected });
  }

  function signup() {
    trackDemoEvent({ type: 'demo_signup_clicked' });
    // la démo se joue « comme un élève » : on garde le profil choisi à l'accueil
    // s'il y en a un (un parent reste parent), sinon élève
    if (!readDecouverteProfile()) saveDecouverteProfile('student');
    markDecouverteSeen();
    void navigate({ to: '/login', search: { inscription: 1 } });
  }

  function login() {
    markDecouverteSeen();
    void navigate({ to: '/login' });
  }

  function quit() {
    if (window.history.length > 1) router.history.back();
    else void navigate({ to: '/' });
  }

  if (phase === 'end') {
    return (
      <div className="min-h-dvh bg-[#061433] sm:flex sm:items-center sm:justify-center sm:py-8">
        <DemoEndScreen
          onSignup={signup}
          onLogin={login}
          restartLabel="Essayer une autre classe"
          onRestart={() => setPhase('level')}
        />
      </div>
    );
  }

  if (phase === 'level' || !level) {
    return (
      <Shell>
        <DemoTopBar label="Démo · sans compte" onClose={quit} />
        <div className="mt-4">
          <LevelSelectionScreen
            onSelect={pickLevel}
            onSignup={signup}
            profile={readDecouverteProfile()}
          />
        </div>
      </Shell>
    );
  }

  const classLabel = LEVEL_LABEL[level];
  const topBar = (
    <DemoTopBar
      label={`${unguided ? 'Une question' : 'Exercice guidé'} · ${classLabel} · sans compte`}
      onClose={quit}
      action={{ label: 'Passer', onClick: () => setPhase('end') }}
    />
  );

  if (loadError) {
    return (
      <Shell>
        {topBar}
        <div className="mx-auto mt-6 max-w-lg space-y-4">
          <div className="bg-error-50 border-error-100 flex items-start gap-2 rounded-xl border px-4 py-3">
            <AlertCircle className="text-error-600 mt-0.5 h-4 w-4 shrink-0" />
            <p className="text-error-700 text-sm">Impossible de charger la question. Réessaie.</p>
          </div>
          <Button onClick={() => setPhase('level')} fullWidth>
            Choisir une autre classe
          </Button>
        </div>
      </Shell>
    );
  }

  if (!question) {
    return (
      <Shell>
        {topBar}
        <div
          className="mx-auto mt-6 max-w-lg space-y-3"
          aria-busy="true"
          data-testid="demo-loading"
        >
          <div className="bg-ink-100 h-6 w-40 animate-pulse rounded" />
          <div className="bg-ink-100 h-32 animate-pulse rounded-2xl" />
          <div className="bg-ink-100 h-12 animate-pulse rounded-xl" />
        </div>
      </Shell>
    );
  }

  if (unguided) {
    return (
      <Shell>
        {topBar}
        <div className="mt-2">
          <ExercisePlayerV2
            key={round}
            exerciseId={question.exercise.exercise.id}
            demoMode
            demoPayload={{
              exercise: {
                ...question.exercise.exercise,
                chapter_id: question.exercise.exercise.chapter_id ?? undefined,
              },
              blocks: question.exercise.blocks,
              access: { allowed: true },
              attempt_id: null,
            }}
            onDemoComplete={finish}
          />
        </div>
      </Shell>
    );
  }

  if (!started)
    return (
      <Shell>
        {topBar}
        <GuidedIntroScreen classLabel={classLabel} onStart={() => setStarted(true)} />
      </Shell>
    );

  return (
    <PaperPage>
      <PaperColumn className="native:pt-[calc(env(safe-area-inset-top)+0.75rem)] pb-8 pt-3">
        {topBar}
        <Suspense
          fallback={
            <p
              className="text-ink-600 mt-6 flex items-center gap-2 text-sm"
              data-testid="demo-guided-loading"
            >
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Préparation de ta feuille…
            </p>
          }
        >
          <GuidedRound
            key={round}
            question={question}
            onFinished={finishGuided}
            onUnavailable={playUnguided}
          />
        </Suspense>
        {/* loin sous la feuille : rien de plus à l'écran pendant l'exercice */}
        <div className="mt-24 flex flex-wrap items-center justify-center gap-x-6">
          <button
            type="button"
            onClick={() => setRound((r) => r + 1)}
            className="text-ink-500 hover:text-ink-700 min-h-11 text-sm font-medium"
          >
            Recommencer l’exercice
          </button>
          <button
            type="button"
            onClick={() => setPhase('level')}
            className="text-ink-500 hover:text-ink-700 min-h-11 text-sm font-medium"
          >
            Changer de classe
          </button>
        </div>
      </PaperColumn>
    </PaperPage>
  );
}

/** Barre du haut de la démo : quitter, ce qu'on fait, une action à droite. */
function DemoTopBar({
  label,
  onClose,
  action,
}: {
  label: string;
  onClose: () => void;
  action?: { label: string; onClick: () => void };
}) {
  return (
    <div className="flex items-center gap-2" data-testid="demo-topbar">
      <button
        type="button"
        onClick={onClose}
        aria-label="Quitter"
        className="text-ink-600 hover:text-ink-900 -ml-2 flex h-11 w-11 shrink-0 items-center justify-center rounded-full"
      >
        <X className="h-5 w-5" aria-hidden />
      </button>
      <p className="text-primary-600 min-w-0 flex-1 truncate text-xs font-bold uppercase tracking-wider">
        {label}
      </p>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="text-primary-600 hover:text-primary-700 min-h-11 shrink-0 px-2 text-sm font-semibold"
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-transparent">
      <div className="native:pt-[env(safe-area-inset-top)] mx-auto max-w-2xl px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] pt-3 sm:py-8">
        {children}
      </div>
    </div>
  );
}
